<script lang="ts" setup>
import {searchAddress} from '@/services/quizService';
import {addressFactory, type IAddress} from '@/types/address';
import {ref, watch} from 'vue';
import {useQuizStore} from "@/stores/quizStore.ts";
import {captureException, captureMessage} from "@sentry/browser";

const emit = defineEmits<{ (e: 'add', address: IAddress): void }>()

const postalCode = ref('');
const addressOptions = ref<IAddress[]>([]);
const address = ref<IAddress | null>(null);
const isFetching = ref(false)

const {showAlert, closeAlert} = useQuizStore()

function findAddresses() {
  if (!postalCode) return
  isFetching.value = true
  addressOptions.value = []
  closeAlert();
  searchAddress(postalCode.value)
    .then((res: any) => {
      addressOptions.value = res.map(addressFactory)
      if (!addressOptions.value.length) {
        showAlert("Postal code not found.")
      }
    })
    .catch(async (err) => {
      const response = await err.response?.json()
      if (response?.error) {
        showAlert(response.error)
      } else {
        captureMessage("Error on searchAddress");
        captureException(err);
      }
    })
    .finally(() => {
      isFetching.value = false
    })
}

watch(address, (newValue) => {
  if (newValue) {
    emit('add', newValue)
    addressOptions.value = []
    address.value = null
    postalCode.value = ''
  }
})
</script>

<template>
  <div class="flex space-x-0 mb-4">
    <input type="text" v-model="postalCode" placeholder="Enter Postcode"
           class="flex-grow w-full p-3 border border-gray-300 rounded-l-lg focus:outline-none"
           :disabled="isFetching">
    <button
      class="bg-green-500 text-white px-4 py-3 rounded-r-lg font-medium cursor-pointer disabled:cursor-not-allowed disabled:text-gray-100 disabled:bg-(--brand-primary)/90 flex gap-2 items-center transition"
      :disabled="isFetching || !postalCode" @click="findAddresses">
      <svg v-if="isFetching" class="mr-3 -ml-1 size-5 animate-spin text-white"
           xmlns="http://www.w3.org/2000/svg"
           fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor"
                stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
        </path>
      </svg>
      <span class="whitespace-nowrap">FIND ADDRESS</span>
    </button>
  </div>

  <select v-if="addressOptions.length" v-model="address"
          class="border border-gray-300 rounded-lg p-3 w-full mb-4 focus:outline-none">
    <option :value="null" disabled selected>Select the address</option>
    <option v-for="address in addressOptions" :value="address"
            :key="`${address.street}, ${address.city}, ${address.state}`">
      {{ address.street }}, {{ address.city }}, {{ address.state }}
    </option>
  </select>
</template>
